import { Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { createXYZ } from "ol/tilegrid"
import { SelectControl, SelectControlOptions } from "../controls/select"
import { booleanDataSocket, categoricalDataSocket, numericDataSocket } from "../socket_types"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid } from "../tile_grid"
import { BaseComponent } from "./base_component"
import { ProjectProperties } from "."

const GRID_TYPE_KEY = "gridType"

const gridTypeOptions: SelectControlOptions[] = [
  { id: 0, name: "Boolean dataset" },
  { id: 1, name: "Numeric dataset" },
  { id: 2, name: "Categorical dataset" },
]

type GridType = "BooleanTileGrid" | "NumericTileGrid" | "CategoricalTileGrid"

function normalizeGridType(value: unknown): GridType {
  if (value === "BooleanTileGrid" || value === "NumericTileGrid" || value === "CategoricalTileGrid") return value

  if (value === "0" || value === 0) return "BooleanTileGrid"
  if (value === "1" || value === 1) return "NumericTileGrid"
  if (value === "2" || value === 2) return "CategoricalTileGrid"

  return "NumericTileGrid"
}

export class CreateEmptyComponent extends BaseComponent {
  projectProps: ProjectProperties

  constructor(projectProps: ProjectProperties) {
    super("Create Empty")
    this.category = "Inputs"
    this.projectProps = projectProps
  }

  async builder(node: Node) {
    // SelectControl option values are numeric ids (0 / 1 / 2). Storing a string like
    // "NumericTileGrid" does not match any <option>, so the UI shows the first option (Boolean)
    // while normalizeGridType still resolves the socket as numeric — keep data aligned with the select.
    const raw = node.data[GRID_TYPE_KEY]
    if (raw === undefined) {
      node.data[GRID_TYPE_KEY] = 1
    } else {
      const normalized = normalizeGridType(raw)
      node.data[GRID_TYPE_KEY] =
        normalized === "BooleanTileGrid" ? 0 : normalized === "NumericTileGrid" ? 1 : 2
    }

    node.addControl(
      new SelectControl(
        this.editor,
        GRID_TYPE_KEY,
        () => gridTypeOptions,
        () => this.updateOutput(node),
        "Grid type"
      )
    )

    this.updateOutput(node)
  }

  updateOutput(node: Node) {
    const current = node.outputs.get("out")
    if (current) {
      const conns = [...current.connections]
      for (const c of conns) {
        this.editor?.removeConnection(c)
      }
      node.removeOutput(current)
    }

    const gridType = normalizeGridType(node.data[GRID_TYPE_KEY])
    switch (gridType) {
      case "BooleanTileGrid":
        node.addOutput(new Output("out", "Empty dataset", booleanDataSocket))
        break
      case "CategoricalTileGrid":
        node.addOutput(new Output("out", "Empty dataset", categoricalDataSocket))
        break
      case "NumericTileGrid":
      default:
        node.addOutput(new Output("out", "Empty dataset", numericDataSocket))
        break
    }
    node.update()
  }

  worker(node: NodeData, _inputs: WorkerInputs, outputs: WorkerOutputs) {
    const tileGrid = createXYZ()
    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(this.projectProps.extent, this.projectProps.zoom)

    const zoom = this.projectProps.zoom
    const x = outputTileRange.minX
    const y = outputTileRange.minY
    const width = outputTileRange.getWidth()
    const height = outputTileRange.getHeight()

    const gridType = normalizeGridType(node.data[GRID_TYPE_KEY])
    const outputName = (node.data.name as string) || "Empty dataset"

    switch (gridType) {
      case "BooleanTileGrid": {
        const grid = new BooleanTileGrid(zoom, x, y, width, height, false)
        grid.name = outputName
        outputs.out = grid
        break
      }
      case "CategoricalTileGrid": {
        const grid = new CategoricalTileGrid(zoom, x, y, width, height, undefined, new Map<number, string>())
        grid.name = outputName
        outputs.out = grid
        break
      }
      case "NumericTileGrid":
      default: {
        const grid = new NumericTileGrid(zoom, x, y, width, height, NaN)
        grid.name = outputName
        outputs.out = grid
        break
      }
    }
  }
}
